import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Toaster, toast } from 'react-hot-toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDoubleLeft, faAngleLeft, faAngleRight, faAngleDoubleRight, faSpinner, faEdit, faTrash, faSave, faTimes, faFilePdf, faDownload, faFileExcel } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import { BASE_URL } from '../../config';

function useQuery() {
  const { search } = useLocation();
  return new URLSearchParams(search);
}

const InterestFeeDetails = () => {
  const { seriesNoUid } = useParams();
  const query = useQuery();
  const series_no = query.get('series');
  const navigate = useNavigate();

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalEntries, setTotalEntries] = useState(0);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [xlsxLoading, setXlsxLoading] = useState(false);

  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({
    net_amount: '',
    vat_amount: '',
    gross_amount: '',
  });

  const phpFormatter = new Intl.NumberFormat('en-PH', {
    style: 'currency',
    currency: 'PHP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  const fetchSeriesDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BASE_URL}/interest-fee/interest-series/${seriesNoUid}`, {
        params: {
          page: currentPage,
          per_page: itemsPerPage,
        },
        headers: {
          Authorization: `Bearer ${localStorage.getItem('authentication/token')}`,
        },
      });
      setRecords(response.data.data);
      setTotalEntries(response.data.pagination.total_records);
      setTotalPages(response.data.pagination.total_pages);
    } catch (error) {
      console.error('Series Details Error:', error.response?.data?.resolved_sql_query || '');
      toast.error('Failed to fetch series details.', { duration: 4000 });
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadFiles = async () => {
    try {
      setPdfLoading(true);
      setXlsxLoading(true);
      const response = await axios.get(`${BASE_URL}/interest-fee/interest-series/${seriesNoUid}/pdfs`, {
        responseType: 'blob',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('authentication/token')}`,
        },
      });

      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/zip' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `InterestFee_Series_${series_no}_${new Date().toISOString().replace(/[:.]/g, '-')}.zip`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('PDFs and XLSX downloaded successfully.', { duration: 4000 });
    } catch (error) {
      console.error('Files Download Error:', error);
      toast.error('Failed to download files.', { duration: 4000 });
    } finally {
      setPdfLoading(false);
      setXlsxLoading(false);
    }
  };

  const handleEditClick = (record) => {
    setEditingId(record.id);
    setEditForm({
      net_amount: record.net_amount,
      vat_amount: record.vat_amount,
      gross_amount: record.gross_amount,
    });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditForm({
      net_amount: '',
      vat_amount: '',
      gross_amount: '',
    });
  };

  const handleEditFormChange = (key, value) => {
    setEditForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSaveEdit = async () => {
    if (!editForm.net_amount || !editForm.vat_amount || !editForm.gross_amount) {
      toast.error('Please fill in all required fields.', { duration: 4000 });
      return;
    }

    try {
      setLoading(true);
      const response = await axios.put(
        `${BASE_URL}/interest-fee/record/${editingId}`,
        {
          net_amount: parseFloat(editForm.net_amount),
          vat_amount: parseFloat(editForm.vat_amount),
          gross_amount: parseFloat(editForm.gross_amount),
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authentication/token')}`,
          },
        }
      );
      toast.success(response.data.message, { duration: 4000 });
      setEditingId(null);
      setEditForm({
        net_amount: '',
        vat_amount: '',
        gross_amount: '',
      });
      fetchSeriesDetails();
    } catch (error) {
      console.error('Update Record Error:', error.response?.data?.resolved_sql_query || '');
      toast.error(error.response?.data?.message || 'Failed to update record.', { duration: 4000 });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this record?')) return;

    try {
      setLoading(true);
      const response = await axios.delete(`${BASE_URL}/interest-fee/record/${id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('authentication/token')}`,
        },
      });
      toast.success(response.data.message, { duration: 4000 });
      fetchSeriesDetails();
    } catch (error) {
      console.error('Delete Record Error:', error.response?.data?.resolved_sql_query || '');
    //   toast.error(error.response?.data?.message || 'Failed to delete record.', { updateRecord Error:', error.response?.data?.resolved_sql_query || '');
      toast.error(error.response?.data?.message || 'Failed to delete record.', { duration: 4000 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSeriesDetails();
  }, [seriesNoUid, currentPage, itemsPerPage]);

  const columns = [
    { key: 'series_no', label: 'Series No.', sortable: true },
    { key: 'payment_id', label: 'Payment ID', sortable: true },
    { key: 'corp_code', label: 'Corp Code', sortable: true },
    { key: 'corp_name', label: 'Corp Name', sortable: true },
    { key: 'billing_date', label: 'Billing Date', sortable: true },
    { key: 'billed_amount', label: 'Billed Amount', sortable: true },
    { key: 'interest_fee', label: 'Interest Fee', sortable: true },
    { key: 'net_amount', label: 'Net Amount', sortable: true },
    { key: 'vat_amount', label: 'VAT Amount', sortable: true },
    { key: 'gross_amount', label: 'Gross Amount', sortable: true },
    { key: 'actions', label: 'Actions', sortable: false },
  ];

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(value);
    setCurrentPage(1);
  };

  const renderPageNumbers = () => {
    const pages = [];
    let startPage = Math.max(1, currentPage - 2);
    let endPage = Math.min(totalPages, currentPage + 2);
    if (currentPage <= 3) endPage = Math.min(5, totalPages);
    else if (currentPage >= totalPages - 2) startPage = Math.max(totalPages - 4, 1);

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`px-3 py-1 rounded-lg text-sm transition-all duration-200 ${
            currentPage === i ? 'bg-indigo-600 text-white' : 'text-gray-700 hover:bg-indigo-100'
          }`}
        >
          {i}
        </button>
      );
    }
    return pages;
  };

  const getShowingEntriesText = () => {
    const start = (currentPage - 1) * itemsPerPage + 1;
    const end = Math.min(currentPage * itemsPerPage, totalEntries);
    return `Showing ${start} to ${end} of ${totalEntries.toLocaleString()} records`;
  };

  const LoadingSkeleton = ({ rows = 5, columns = 11 }) => (
    <>
      {Array(rows)
        .fill()
        .map((_, i) => (
          <tr key={i} className="animate-pulse border-b">
            {Array(columns)
              .fill()
              .map((_, j) => (
                <td key={j} className="p-3">
                  <div className="h-4 bg-gray-200 rounded w-full"></div>
                </td>
              ))}
          </tr>
        ))}
    </>
  );

  return (
    <div className="min-h-screen bg-gray-100 p-4">
      <Toaster position="top-right" toastOptions={{ duration: 4000 }} />
      <div className="max-w-1xl mx-auto">
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => navigate('/billing/interest-fee-billed')}
            className="p-3 bg-white rounded-2xl shadow-lg"
          >
            <FontAwesomeIcon icon={faAngleLeft} className="text-indigo-600 text-lg" />
          </button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-gray-900">Series {series_no} Details</h1>
            <p className="text-sm text-gray-500">View and manage records for series {series_no}</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleDownloadFiles}
              className="flex items-center gap-2 px-4 py-2 text-sm text-white bg-red-600 hover:bg-red-700 rounded-lg disabled:bg-red-400 disabled:cursor-not-allowed"
              disabled={pdfLoading || xlsxLoading}
            >
              <FontAwesomeIcon icon={pdfLoading ? faSpinner : faDownload} spin={pdfLoading} />
              <FontAwesomeIcon icon={faFilePdf} />
              Download PDF
            </button>
            <button
              onClick={handleDownloadFiles}
              className="flex items-center gap-2 px-4 py-2 text-sm text-white bg-green-600 hover:bg-green-700 rounded-lg disabled:bg-green-400 disabled:cursor-not-allowed"
              disabled={pdfLoading || xlsxLoading}
            >
              <FontAwesomeIcon icon={xlsxLoading ? faSpinner : faDownload} spin={xlsxLoading} />
              <FontAwesomeIcon icon={faFileExcel} />
              Print Out with XLSX
            </button>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          <div className="p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Series {series_no} Records</h2>
            <div className="overflow-x-auto">
              <table className="w-full table-auto">
                <thead className="bg-gray-50">
                  <tr>
                    {columns.map((column) => (
                      <th
                        key={column.key}
                        className="p-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider"
                      >
                        {column.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {loading ? (
                    <LoadingSkeleton rows={itemsPerPage} columns={columns.length} />
                  ) : records.length === 0 ? (
                    <tr>
                      <td colSpan={columns.length} className="p-3 text-center text-gray-500 text-sm">
                        No records found.
                      </td>
                    </tr>
                  ) : (
                    records.map((record) => (
                      <tr key={record.id} className="border-b hover:bg-gray-50">
                        <td className="p-3 text-sm">{record.series_no}</td>
                        <td className="p-3 text-sm">{record.payment_id}</td>
                        <td className="p-3 text-sm">{record.corp_code}</td>
                        <td className="p-3 text-sm">{record.corp_name}</td>
                        <td className="p-3 text-sm">{record.billing_date}</td>
                        <td className="p-3 text-sm">{phpFormatter.format(record.billed_amount)}</td>
                        <td className="p-3 text-sm">{phpFormatter.format(record.interest_fee)}</td>
                        <td className="p-3 text-sm">
                          {editingId === record.id ? (
                            <input
                              type="number"
                              value={editForm.net_amount}
                              onChange={(e) => handleEditFormChange('net_amount', e.target.value)}
                              className="w-full px-2 py-1 text-sm border rounded"
                              min="0"
                              step="0.01"
                            />
                          ) : (
                            phpFormatter.format(record.net_amount)
                          )}
                        </td>
                        <td className="p-3 text-sm">
                          {editingId === record.id ? (
                            <input
                              type="number"
                              value={editForm.vat_amount}
                              onChange={(e) => handleEditFormChange('vat_amount', e.target.value)}
                              className="w-full px-2 py-1 text-sm border rounded"
                              min="0"
                              step="0.01"
                            />
                          ) : (
                            phpFormatter.format(record.vat_amount)
                          )}
                        </td>
                        <td className="p-3 text-sm">
                          {editingId === record.id ? (
                            <input
                              type="number"
                              value={editForm.gross_amount}
                              onChange={(e) => handleEditFormChange('gross_amount', e.target.value)}
                              className="w-full px-2 py-1 text-sm border rounded"
                              min="0"
                              step="0.01"
                            />
                          ) : (
                            phpFormatter.format(record.gross_amount)
                          )}
                        </td>
                        <td className="p-3 text-sm flex gap-2">
                          {editingId === record.id ? (
                            <>
                              <button
                                onClick={handleSaveEdit}
                                className="text-green-600 hover:text-green-800"
                                disabled={loading}
                              >
                                <FontAwesomeIcon icon={faSave} />
                              </button>
                              <button
                                onClick={handleCancelEdit}
                                className="text-gray-600 hover:text-gray-800"
                                disabled={loading}
                              >
                                <FontAwesomeIcon icon={faTimes} />
                              </button>
                            </>
                          ) : (
                            <>
                              {/* <button
                                onClick={() => handleEditClick(record)}
                                className="text-blue-600 hover:text-blue-800"
                                disabled={loading}
                              >
                                <FontAwesomeIcon icon={faEdit} />
                              </button>
                              <button
                                onClick={() => handleDelete(record.id)}
                                className="text-red-600 hover:text-red-800"
                                disabled={loading}
                              >
                                <FontAwesomeIcon icon={faTrash} />
                              </button> */}
                            </>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="p-6 border-t border-gray-100">
            <div className="flex flex-col md:flex-row md:items-center justify-between">
              <div className="flex items-center gap-4 mb-3 md:mb-0">
                <select
                  value={itemsPerPage}
                  onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                  className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  aria-label="Items per page"
                >
                  {[5, 10, 20, 50, 100].map((val) => (
                    <option key={val} value={val}>
                      {val} per page
                    </option>
                  ))}
                </select>
                <span className="text-sm text-gray-600">{getShowingEntriesText()}</span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1 || loading}
                  className="p-2 text-gray-700 rounded-lg hover:bg-indigo-100 disabled:opacity-50"
                  aria-label="First page"
                >
                  <FontAwesomeIcon icon={faAngleDoubleLeft} />
                </button>
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1 || loading}
                  className="p-2 text-gray-700 rounded-lg hover:bg-indigo-100 disabled:opacity-50"
                  aria-label="Previous page"
                >
                  <FontAwesomeIcon icon={faAngleLeft} />
                </button>
                {renderPageNumbers()}
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages || loading}
                  className="p-2 text-gray-700 rounded-lg hover:bg-indigo-100 disabled:opacity-50"
                  aria-label="Next page"
                >
                  <FontAwesomeIcon icon={faAngleRight} />
                </button>
                <button
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage === totalPages || loading}
                  className="p-2 text-gray-700 rounded-lg hover:bg-indigo-100 disabled:opacity-50"
                  aria-label="Last page"
                >
                  <FontAwesomeIcon icon={faAngleDoubleRight} />
                </button>
              </div>
            </div>
          </div>
        </div>

        {(pdfLoading || xlsxLoading) && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 flex flex-col items-center gap-4">
              <FontAwesomeIcon icon={faSpinner} className="text-indigo-600 text-2xl animate-spin" />
              <p className="text-gray-800 text-lg">Please wait while generating the files...</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InterestFeeDetails;